import { INSPECTION_STATUS_LABEL } from '@obraplan/core/domain/planning/daily-inspection';
import { quantityBalance } from '@obraplan/core/domain/planning/measurement';
import { formatQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import { Hours } from '@obraplan/core/domain/shared/hours';
import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';

/**
 * A folha de conferência — o impresso do **depois**.
 *
 * Não é a programação em papel: aquela sai da tela de Programação do dia, diz o que vai
 * acontecer e serve para mandar gente trabalhar. Esta diz **o que aconteceu** e serve para
 * ser assinada.
 *
 * A diferença aparece nas colunas que só existem aqui: ao lado do que estava combinado vem
 * o que saiu, e a diferença entre os dois. É a pergunta que o dono da obra faz — planejado
 * × executado —, e é o que justifica um segundo documento sobre o mesmo dia.
 *
 * Nada aqui consulta nada: recebe as mesmas linhas que a tela já carregou. É uma segunda
 * apresentação do dado da conferência, não uma segunda verdade sobre ele.
 *
 * Fica sempre montada e escondida na tela (`hidden print:block`), o que faz o `Ctrl+P` do
 * navegador imprimir a folha sem que ninguém precise achar um botão.
 */
export interface InspectionReportProps {
  dateLabel: string;
  projectName: string | undefined;
  lines: readonly InspectionLine[];
}

export function InspectionReport({ dateLabel, projectName, lines }: InspectionReportProps) {
  const totalQuarters = lines.reduce((sum, line) => sum + line.plannedQuarterHours, 0);
  const grouped = groupByTeam(lines);

  return (
    <article className="hidden text-black print:block">
      <header className="mb-4 border-b border-black pb-2">
        <h1 className="text-lg font-bold">Conferência — {dateLabel}</h1>
        {projectName !== undefined && <p className="text-sm">Obra: {projectName}</p>}
        <p className="text-sm">
          {lines.length} {lines.length === 1 ? 'funcionário' : 'funcionários'} ·{' '}
          {Hours.ofQuarters(totalQuarters).format()} planejadas
        </p>
      </header>

      {lines.length === 0 ? (
        // O impresso não sabe se a lista veio vazia por não haver trabalho ou por um filtro
        // na tela; "nenhum funcionário nesta seleção" é verdade nos dois casos.
        <p className="text-sm">Nenhum funcionário nesta seleção.</p>
      ) : (
        grouped.map((group) => (
          <section key={group.team} className="mb-4">
            <h2 className="mb-1 text-xs font-bold uppercase">{group.team}</h2>

            {group.lines.map((line) => (
              <div
                key={line.resource.resourceId}
                className="mb-2 border-b border-dotted border-black pb-2"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-semibold">
                    {line.resource.name}
                    <span className="font-normal"> — {line.resource.detail}</span>
                  </p>
                  <p className="tabular text-sm font-semibold">
                    {Hours.ofQuarters(line.plannedQuarterHours).format()}
                  </p>
                </div>

                {line.allocations.length === 0 ? (
                  <p className="pl-4 text-xs">Sem atividade planejada.</p>
                ) : (
                  <table className="mt-1 w-full text-xs">
                    <thead>
                      <tr className="text-left">
                        <th className="pl-4 font-normal">Frente</th>
                        <th className="text-right font-normal">Horas</th>
                        <th className="text-right font-normal">Planejado</th>
                        <th className="text-right font-normal">Executado</th>
                        <th className="text-right font-normal">Diferença</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.allocations.map(({ activity, allocation, unit }) => {
                        const balance = quantityBalance(
                          allocation.plannedQuantity,
                          allocation.executedQuantity,
                        );
                        const difference = balance.difference;

                        return (
                          <tr key={allocation.id}>
                            <td className="py-0.5 pl-4">
                              {activity.label} · {activity.towerName}
                            </td>
                            <td className="tabular py-0.5 text-right">
                              {Hours.ofQuarters(allocation.quarterHours).format()}
                            </td>
                            <td className="tabular py-0.5 text-right">
                              {unit === undefined || allocation.plannedQuantity === undefined
                                ? '—'
                                : formatQuantity(allocation.plannedQuantity, unit)}
                            </td>
                            <td className="tabular py-0.5 text-right">
                              {/*
                                Frente medida e ainda sem medição sai com a linha para
                                escrever: quem confere no papel anota na hora e transcreve
                                depois. Sem ela, mediria de cabeça.
                              */}
                              {unit === undefined
                                ? '—'
                                : allocation.executedQuantity === undefined
                                  ? '________'
                                  : formatQuantity(allocation.executedQuantity, unit)}
                            </td>
                            <td className="tabular py-0.5 text-right">
                              {unit === undefined || difference === undefined
                                ? '—'
                                : `${difference > 0 ? '+' : difference < 0 ? '−' : ''}${formatQuantity(Math.abs(difference), unit)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                <p className="pl-4 pt-1 text-xs">
                  {line.inspection === undefined || line.inspection.status === 'PENDING'
                    ? '[   ] Confirmado    [   ] Ajustado    [   ] Ausente'
                    : `Conferido: ${INSPECTION_STATUS_LABEL[line.inspection.status]}`}
                </p>
              </div>
            ))}
          </section>
        ))
      )}

      <footer className="mt-6 border-t border-black pt-2 text-xs">
        <p>Conferido por: ______________________________ Assinatura: ______________________</p>
      </footer>
    </article>
  );
}

interface TeamGroup {
  readonly team: string;
  readonly lines: InspectionLine[];
}

/** Por equipe, preservando a ordem que veio do domínio. Quem não tem equipe fecha a folha. */
function groupByTeam(lines: readonly InspectionLine[]): TeamGroup[] {
  const groups = new Map<string, InspectionLine[]>();
  for (const line of lines) {
    const team = line.resource.teamName ?? 'Sem equipe';
    const current = groups.get(team);
    if (current === undefined) groups.set(team, [line]);
    else current.push(line);
  }

  const entries = [...groups.entries()].map(([team, grouped]) => ({ team, lines: grouped }));
  // "Sem equipe" vai para o fim, sempre: é o resto, não uma turma.
  return entries.sort((a, b) => {
    if (a.team === b.team) return 0;
    if (a.team === 'Sem equipe') return 1;
    if (b.team === 'Sem equipe') return -1;
    return a.team.localeCompare(b.team, 'pt-BR');
  });
}
